import { Controller } from "@hotwired/stimulus"

// Drives the public landing connect flow (PΔ.237: landing v2, dark theme).
//
// The user pastes any combination of share links + surrounding text into the
// HERO form; this controller POSTs the raw string to /onboarding/preview,
// renders the parsed preview card (dark .ob-* classes styled by the landing's
// own <style>), and on "Connect Telegram" fires /onboarding/init and shows
// the t.me deep links. The FINAL-CTA form at the bottom of the page proxies
// into the hero form (previewSecondary) so preview/connect render in one
// place, right under the hero.
//
// Targets:
//   form            the hero <form>
//   input           the hero <textarea>
//   secondaryInput  the bottom form's <input> (optional)
//   submit          hero submit <button>
//   error           inline error <p>
//   preview         container for the platform preview card
//   connect         container for the "open Telegram" step
//
// Values:
//   previewUrl / initUrl        endpoints
//   submitText / submittingText button states
//   dict                        i18n dictionary (landing.js.* keys) — the
//                               JS renders localized markup from here, no
//                               hardcoded strings (PΔ.237: EN/ID/RU landing)
//
// PΔ.237 — UTM capture: utm_* / ref query params are read on connect() and
// passed into /onboarding/init, which stores them on the invite
// (preview_snapshot["utm"]) for funnel source attribution.
export default class extends Controller {
  static targets = ["form", "input", "secondaryInput", "submit", "error", "preview", "connect"]
  static values  = {
    previewUrl: String,
    initUrl: String,
    submitText: String,
    submittingText: String,
    dict: Object
  }

  connect() {
    this.previewToken = null
    this.platforms = null
    this.utm = this.captureUtm()
  }

  // PΔ.237.2 — visual clean-up of pasted share messages. Platform "Share"
  // text arrives as «Check out X on GrabFood! https://r.grab.com/…» — after
  // a paste we strip the words and keep ONLY the links (one per line), so
  // the field shows exactly what will be connected. A second paste appends:
  // we re-extract over the whole field, so both links survive. If no URL is
  // found we leave the text untouched (the server-side extractor stays the
  // source of truth — this is purely cosmetic).
  cleanInput(event) {
    const field = event.target
    setTimeout(() => {
      const urls = (field.value.match(/https?:\/\/[^\s]+/g) || [])
        .map(u => u.replace(/[),.;!?]+$/, "")) // trailing punctuation from prose
      if (!urls.length) return
      const unique = [...new Set(urls)]
      // textarea shows one link per line; the single-line bottom input
      // separates with a space (newlines don't render in <input>).
      field.value = unique.join(field.tagName === "TEXTAREA" ? "\n" : " ")
      if (field.tagName === "TEXTAREA") field.rows = Math.min(unique.length, 3)
    }, 0)
  }

  captureUtm() {
    const utm = {}
    try {
      const params = new URLSearchParams(window.location.search)
      for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "ref"]) {
        const v = params.get(key)
        if (v) utm[key] = String(v).slice(0, 100)
      }
    } catch (_e) { /* no-op — attribution must never break the form */ }
    return utm
  }

  t(key, fallback = "") {
    const d = this.dictValue || {}
    return d[key] || fallback || key
  }

  async preview(event) {
    event.preventDefault()
    this.hideError()
    const raw = this.inputTarget.value.trim()
    if (!raw) {
      this.showError(this.t("err_url_required"))
      return
    }

    this.setSubmitting(true)
    try {
      const res = await this.postJSON(this.previewUrlValue, { input: raw })
      const body = await res.json()

      if (!res.ok) {
        this.showError(this.humanizeError(body.error))
        return
      }

      this.previewToken = body.preview_token
      this.platforms = body.platforms
      this.orgChoices = body.org_choices || [] // PΔ.173 — present only for multi-org users
      // PΔ.281.1 — «похоже, это заведение вы уже добавляли». Приходит и
      // анонимному посетителю, если карточку заводил ЭТОТ ЖЕ браузер.
      this.dupCandidate = body.dup_candidate || null
      this.renderPreview(body)
    } catch (err) {
      console.error("[onboarding] preview failed", err)
      this.showError(this.t("err_network"))
    } finally {
      this.setSubmitting(false)
    }
  }

  // PΔ.237 — the final-CTA form: copy its value into the hero form, run the
  // same preview, scroll up to where the preview card renders.
  previewSecondary(event) {
    event.preventDefault()
    if (this.hasSecondaryInputTarget) {
      const raw = this.secondaryInputTarget.value.trim()
      if (raw) this.inputTarget.value = raw
    }
    this.preview(event)
    this.element.querySelector("#connect")?.scrollIntoView({ behavior: "smooth" })
  }

  async connectTelegram() {
    if (!this.previewToken) return
    this.setSubmitting(true)
    try {
      const payload = {
        preview_token: this.previewToken,
        channel: "telegram"
      }
      if (this.utm && Object.keys(this.utm).length) payload.utm = this.utm
      // PΔ.173 — explicit org choice (radio group rendered only for multi-org users)
      const picked = this.element.querySelector("input[name='onboarding_org']:checked")
      if (picked) {
        if (picked.value === "__new__") payload.org_mode = "new"
        else payload.target_org_id = picked.value
      }
      // PΔ.281.1 — галочка «это то же заведение» стоит по умолчанию: почти
      // всегда так и есть, а снявший её получает отдельную карточку, как
      // раньше.
      const attach = this.element.querySelector("input[name='onboarding_attach']")
      if (attach && attach.checked && this.dupCandidate) {
        payload.attach_to_restaurant_id = this.dupCandidate.id
      }
      const res = await this.postJSON(this.initUrlValue, payload)
      const body = await res.json()

      if (!res.ok) {
        this.showError(this.humanizeError(body.error))
        return
      }

      this.renderStep2(body)
    } catch (err) {
      console.error("[onboarding] init failed", err)
      this.showError(this.t("err_could_not_create_invite"))
    } finally {
      this.setSubmitting(false)
    }
  }

  // --- rendering (dark .ob-* classes; see landing.html.erb <style>) ---

  renderPreview(body) {
    const platforms = body.platforms || {}
    const errors = body.errors || {}

    const card = (key, label) => {
      const p = platforms[key]
      if (!p) {
        if (errors[key]) {
          return `
            <div class="ob-plat err">
              <div class="pl">${label}</div>
              <div class="pm">${this.t("couldnt_load")} ${this.escape(this.humanizeError(errors[key]))}</div>
            </div>`
        }
        return ""
      }
      return `
        <div class="ob-plat">
          <div class="pl">${label}</div>
          <div class="pn">${this.escape(p.name || this.t("unnamed"))}</div>
          <div class="pm">★ ${this.escape(String(p.rating || "—"))} · ${Number(p.menu_count || 0)} ${this.t("items")}</div>
        </div>`
    }

    this.previewTarget.innerHTML = `
      <div class="ob-card">
        <div class="ob-kicker">${this.t("found")}</div>
        <div class="ob-title">${this.escape(body.name || this.t("your_restaurant"))}</div>
        <div class="ob-grid">
          ${card("grab", "GrabFood")}
          ${card("gojek", "GoFood")}
        </div>
        ${this.dupChoiceBlock()}
        ${this.orgChoiceBlock()}
        <div class="ob-actions">
          <button type="button" class="btn btn-primary" style="padding:11px 20px;font-size:14px"
                  data-action="onboarding#connectTelegram">
            ${this.t("connect_telegram")}
          </button>
          <button type="button" class="ob-link" data-action="onboarding#reset">
            ${this.t("try_another")}
          </button>
        </div>
      </div>`
    this.previewTarget.classList.remove("hidden")
    this.formTarget.classList.add("hidden")
  }

  renderStep2(body) {
    const platformsList = (body.platforms || []).map(p => p === "gojek" ? "GoFood" : "GrabFood").join(" + ")
    const dmLink    = body.dm_deep_link    || body.deep_link.replace("?startgroup=", "?start=").split("&")[0]
    const groupLink = body.group_deep_link || body.deep_link
    const lead = this.t("step2_lead")
      .replace("%{platforms}", `<strong>${platformsList}</strong>`)
      .replace("%{name}", `<strong>${this.escape(body.restaurant_name || this.t("your_restaurant"))}</strong>`)

    this.connectTarget.innerHTML = `
      <div class="ob-card">
        <div class="ob-kicker">${this.t("step2_kicker")}</div>
        <div class="ob-title">${this.t("step2_title")}</div>
        <p style="font-size:14px;color:var(--muted)">${lead}</p>

        <a href="${dmLink}" target="_blank" class="ob-tg">
          <div class="t">${this.t("dm_title")}</div>
          <div class="d">${this.t("dm_desc")}</div>
        </a>

        <a href="${groupLink}" target="_blank" class="ob-tg alt">
          <div class="t">${this.t("group_title")}</div>
          <div class="d">${this.t("group_desc")}</div>
        </a>

        <p class="ob-note">${this.t("expires")}</p>
      </div>`
    this.connectTarget.classList.remove("hidden")
    this.previewTarget.classList.add("hidden")
  }

  // PΔ.281.1 — «похоже, это то же заведение». Показываем ТОЛЬКО когда сервер
  // прислал кандидата: узнанному человеку — по его организациям, анонимному —
  // по его же прежним заявкам из этого браузера. Галочка стоит по умолчанию,
  // но снять её можно: у соседних точек сети имена совпадают.
  dupChoiceBlock() {
    const dup = this.dupCandidate
    if (!dup || !dup.id) return ""

    const line = this.t("dup_line").replace("%{name}", `<strong>${this.escape(dup.name || "")}</strong>`)
    return `
      <div class="ob-org">
        <div class="ob-kicker">${this.t("dup_block")}</div>
        <label>
          <input type="checkbox" name="onboarding_attach" checked>
          ${line}
        </label>
        <p class="ob-note">${this.t("dup_hint")}</p>
      </div>`
  }

  // PΔ.173 — org selector for multi-org users. Empty string for everyone
  // else (regular clients with 0-1 orgs never see this).
  orgChoiceBlock() {
    const choices = this.orgChoices || []
    if (choices.length < 2) return ""

    const options = choices.map((o, i) => `
      <label>
        <input type="radio" name="onboarding_org" value="${this.escape(String(o.id))}" ${i === 0 ? "checked" : ""}>
        ${this.escape(o.name || o.slug)}
      </label>`).join("")

    return `
      <div class="ob-org">
        <div class="ob-kicker">${this.t("org_block")}</div>
        ${options}
        <label>
          <input type="radio" name="onboarding_org" value="__new__">
          ${this.t("org_new")}
        </label>
      </div>`
  }

  reset() {
    this.previewTarget.classList.add("hidden")
    this.connectTarget.classList.add("hidden")
    this.previewTarget.innerHTML = ""
    this.connectTarget.innerHTML = ""
    this.formTarget.classList.remove("hidden")
    this.inputTarget.focus()
    this.previewToken = null
  }

  // --- helpers ---

  setSubmitting(yes) {
    this.submitTarget.disabled = yes
    this.submitTarget.textContent = yes ? this.submittingTextValue : this.submitTextValue
  }

  showError(text) {
    this.errorTarget.textContent = text
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorTarget.classList.add("hidden")
  }

  humanizeError(code) {
    const map = {
      url_required: this.t("err_url_required"),
      unknown_platform: this.t("err_unknown_platform"),
      not_found: this.t("err_not_found"),
      parser_unavailable: this.t("err_parser_unavailable"),
      preview_expired: this.t("err_preview_expired"),
      could_not_create_invite: this.t("err_could_not_create_invite")
    }
    return map[code] || (code || this.t("err_generic"))
  }

  escape(s) {
    return String(s).replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"}[c]))
  }

  postJSON(url, body) {
    const csrfToken = document.querySelector("meta[name=csrf-token]")?.content
    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken || ""
      },
      body: JSON.stringify(body)
    })
  }
}
